<div class="flex flex-col h-full bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative select-none"
     (mouseup)="onMouseUp()">

  <!-- Column Header -->
  <div class="px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex items-center gap-2 flex-shrink-0">
    <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
    <h2 class="font-semibold text-gray-700 text-sm">Time Entries</h2>
    <span class="text-xs text-gray-400 ml-auto">Drag to log time</span>
  </div>

  <!-- Scrollable Timeline Grid -->
  <div #scrollContainer
       class="flex-1 overflow-y-auto relative custom-scrollbar"
       (mousemove)="onContainerMouseMove($event)"
       (mouseleave)="onMouseLeaveGrid()">
    <div class="flex flex-col min-h-full relative min-w-80">

      <!-- Current Time Indicator (NOW line) -->
      @if(currentLineTop() > 0) {
        <div class="absolute w-full z-40 pointer-events-none flex items-center"
            [style.top.px]="currentLineTop() - 1">
          <div class="flex-1 h-0.5 bg-gradient-to-r from-red-300 to-red-500"></div>
        </div>
      }

      <!-- Time Slots Grid -->
      @for (slot of slots(); track slot.index) {
        <div class="flex relative group"
             [style.height.px]="rowHeight()"
             (mousedown)="onMouseDown(slot.index)"
             (mouseenter)="onMouseEnter(slot.index)"
             [class.bg-blue-50]="hoveredSlotIndex() === slot.index && !selectionSlots().has(slot.index)"
             [class.bg-gray-50]="slot.isHourMark && hoveredSlotIndex() !== slot.index && !selectionSlots().has(slot.index)">

          <!-- Time Label Column -->
          <div #hourColumn
               class="w-[60px] text-right pr-3 text-xs border-r flex items-center justify-end pointer-events-none select-none"
               [class.border-gray-200]="slot.isHourMark"
               [class.border-gray-100]="!slot.isHourMark"
               [class.border-b]="true"
               [class.border-b-gray-200]="slot.isHourMark"
               [class.border-b-gray-100]="!slot.isHourMark"
               [class.font-semibold]="slot.isHourMark"
               [class.text-gray-700]="slot.isHourMark"
               [class.text-gray-400]="!slot.isHourMark"
               [class.bg-gray-50]="slot.isHourMark">
            {{ slot.label }}
          </div>

          <!-- Entry Area -->
          <div class="flex-1 relative border-b"
               [class.border-gray-200]="slot.isHourMark"
               [class.border-gray-100]="!slot.isHourMark"
               [class.cursor-crosshair]="!entriesPerSlot().get(slot.index)?.length">

            <!-- Selection Highlight -->
            @if (selectionSlots().has(slot.index)) {
              <div class="absolute inset-0 bg-blue-500/20 z-10 border-l-2 border-blue-500"></div>
            }

            <!-- Empty Slot Guide Line -->
            @if(!selectionSlots().has(slot.index)) {
              <div class="absolute inset-0 flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div class="w-full h-px bg-blue-200"></div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Time Entry Blocks (Positioned Absolutely) -->
      @for (entry of entriesWithLanes().entries; track $index) {
        @if (entry.id) {
        <div class="absolute cursor-pointer overflow-hidden rounded-lg
                    hover:brightness-95 hover:shadow-lg hover:scale-[1.02] hover:z-30
                    transition-all duration-150 shadow-md
                    border border-white/50 group/entry"
             [style.top.px]="placements().get(entry.id)?.top"
             [style.height.px]="placements().get(entry.id)?.height"
             [style.left]="placements().get(entry.id)?.left"
             [style.width]="placements().get(entry.id)?.width"
             [style.background]="entry.color"
             (click)="openDetail(entry, $event)">

          <!-- Entry Content -->
          <div class="h-full p-2 flex flex-col justify-center">
            <!-- Header: Project + Duration -->
            <div class="flex items-center justify-between gap-2">
              <span class="font-semibold text-xs text-gray-900 truncate">{{ entry.projectName }}</span>
              <span class="text-[10px] font-medium text-gray-700 bg-white/50 px-1.5 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                {{ placements().get(entry.id)?.duration }}
              </span>
            </div>

            <!-- Description (if enough height) -->
            @if ((placements().get(entry.id)?.height ?? 0) > 40 && entry.content) {
              <div class="text-[10px] text-gray-600 mt-1 line-clamp-2 opacity-80">{{ entry.content }}</div>
            }
          </div>

          <!-- Hover Actions -->
          <div class="absolute top-1 right-1 opacity-0 group-hover/entry:opacity-100 transition-opacity">
            <button class="p-1 bg-white/80 rounded hover:bg-white shadow-sm"
                    title="Edit entry">
              <svg class="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
              </svg>
            </button>
          </div>
        </div>
        }
      }

      <!-- Google Calendar Event Blocks -->
      @for (ev of calendarPlacements(); track ev.id) {
        <div class="absolute overflow-hidden rounded-lg cursor-pointer z-30
                    border border-blue-300 bg-blue-100/90
                    hover:bg-blue-200 hover:shadow-md transition-all duration-150 group/cal"
             [style.top.px]="ev.top"
             [style.height.px]="ev.height"
             style="right: 0; width: 140px;"
             (click)="selectionMade.emit({ startTime: ev.startTime, endTime: ev.endTime })">
          <div class="h-full px-2 py-1 flex flex-col justify-center">
            <div class="flex items-center gap-1">
              <svg class="w-2.5 h-2.5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5C3.9 3 3 3.9 3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V9h14v10z"/>
              </svg>
              <span class="font-semibold text-[10px] text-blue-800 truncate">{{ ev.summary }}</span>
            </div>
            @if (ev.height > 40) {
              <span class="text-[9px] text-blue-500 mt-0.5">{{ ev.durationStr }}</span>
            }
          </div>
          <!-- click hint -->
          <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover/cal:opacity-100
                      bg-blue-500/10 transition-opacity">
            <span class="text-[9px] text-blue-700 font-medium bg-white/80 px-1.5 py-0.5 rounded">Log time</span>
          </div>
        </div>
      }

      <!-- Empty State Overlay (when no entries AND no calendar events for the day) -->
      @if(entriesWithLanes().entries.length === 0 && calendarPlacements().length === 0) {
        <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20 bg-white/50">
          <svg class="w-16 h-16 text-gray-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span class="text-sm text-gray-400 font-medium">No time entries</span>
          <span class="text-xs text-gray-300 mt-1">Drag on the grid to log time</span>
        </div>
      }
    </div>
  </div>
</div>

@if (selectedEntry()) {
  <app-mini-modal-detail-entry
    [entry]="selectedEntry()!"
    [position]="modalPosition()"
    (close)="closeDetail()">
  </app-mini-modal-detail-entry>
}
